import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import { chaves } from './chaves'
import { paraConsulta, type FiltrosDaLista } from '../filtrosDeGmud'
import type {
  Anexo,
  CategoriaDeAnexo,
  AcaoDisponivel,
  CampoDoPlano,
  DetalheGmud,
  ExecutarTransicaoRequest,
  NovaGmudRequest,
  Pagina,
  ParecerDeGmud,
  RegistroTransicao,
  ResultadoTransicao,
  ResumoGmud,
  RevisaoDePr,
  NotaDaGmud,
  JanelaDoComite,
} from '../../types/gmud'

/**
 * Corpo de `PATCH /gmuds/{id}` — espelha o `PlanoRequest` do backend.
 *
 * Campo ausente MANTÉM o que está gravado; para apagar um campo, mande string vazia. É o que
 * permite salvar o formulário por partes sem reenviar o plano inteiro a cada digitação. Cuidado
 * com a diferença: `{ justificativa: undefined }` não apaga nada, `{ justificativa: '' }` apaga.
 *
 * Cronograma e recursos são `jsonb` e ficam de fora enquanto nenhuma tela os edita — declará-los
 * como `unknown` aqui só serviria para alguém mandar uma string e o banco recusar no meio do
 * UPDATE, que é o erro que não vira aviso de formulário.
 */
export type EdicaoDoPlano = Partial<Record<CampoDoPlano, string>>

/**
 * Lista paginada, com os filtros que estão na URL.
 *
 * `placeholderData` mantém a página anterior visível durante a busca da próxima: sem
 * isso a tabela pisca e a tabela vazia de meio segundo é lida como "acabou".
 */
export function useListaDeGmuds(filtros: FiltrosDaLista) {
  return useQuery({
    queryKey: chaves.lista(filtros),
    queryFn: async () =>
      (await api.get<Pagina<ResumoGmud>>('/gmuds', { params: paraConsulta(filtros) })).data,
    placeholderData: (anterior) => anterior,
  })
}

/** Detalhe de uma solicitação. */
export function useGmud(id: number) {
  return useQuery({
    queryKey: chaves.detalhe(id),
    queryFn: async () => (await api.get<DetalheGmud>(`/gmuds/${id}`)).data,
    enabled: Number.isFinite(id),
  })
}

/**
 * O que ESTE usuário pode fazer com ESTA solicitação, agora.
 *
 * É a única fonte dos botões de ação. A tela não deriva ação de status nem de papel —
 * foi assim que o sistema anterior passou a oferecer "Aprovar" para quem o backend
 * recusava, e a acumular três listas divergentes das mesmas regras.
 */
export function useAcoesDisponiveis(id: number) {
  return useQuery({
    queryKey: chaves.acoes(id),
    queryFn: async () => (await api.get<AcaoDisponivel[]>(`/gmuds/${id}/transicoes-possiveis`)).data,
    enabled: Number.isFinite(id),
    // A decisão do comitê pode chegar enquanto a tela está aberta; 30s de cache basta.
    staleTime: 30_000,
  })
}

/** Trilha de transições, da mais antiga para a mais recente. */
export function useHistoricoDeTransicoes(id: number) {
  return useQuery({
    queryKey: chaves.historico(id),
    queryFn: async () => (await api.get<RegistroTransicao[]>(`/gmuds/${id}/transicoes`)).data,
    enabled: Number.isFinite(id),
  })
}

/**
 * Executa um comando de transição.
 *
 * Invalida TUDO de GMUD ao concluir. Poderia invalidar só as três chaves da tela, mas
 * a lista de trás também mudou (o registro trocou de estado) e um cache seletivo aqui
 * é exatamente o tipo de otimização que envelhece errado quando alguém acrescenta uma
 * consulta nova.
 */
export function useExecutarTransicao(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (pedido: ExecutarTransicaoRequest) =>
      (await api.post<ResultadoTransicao>(`/gmuds/${id}/transicoes`, pedido)).data,
    onSuccess: (resultado) => {
      cliente.setQueryData(chaves.detalhe(id), resultado.gmud)
      cliente.setQueryData(chaves.acoes(id), resultado.acoes)
      void cliente.invalidateQueries({ queryKey: chaves.gmuds() })
    },
  })
}

/**
 * Salva o plano de uma solicitação ainda editável.
 *
 * Invalida TUDO de GMUD, pelo mesmo motivo de `useExecutarTransicao` — e aqui há um segundo: as
 * pendências mudam a cada campo preenchido, e são elas que decidem se "Enviar ao comitê" aparece
 * habilitado em `/transicoes-possiveis`. Guardar só o detalhe deixaria o botão dizendo que falta
 * a justificativa que o usuário acabou de escrever.
 *
 * O 422 de plano congelado não é tratado aqui: a mensagem do servidor já explica que a solicitação
 * foi ao comitê e que o que foi aprovado é o que tem de ser executado. Quem chama mostra o erro.
 */
export function useEditarPlano(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (plano: EdicaoDoPlano) => (await api.patch<DetalheGmud>(`/gmuds/${id}`, plano)).data,
    onSuccess: (atualizada) => {
      cliente.setQueryData(chaves.detalhe(id), atualizada)
      void cliente.invalidateQueries({ queryKey: chaves.gmuds() })
    },
  })
}

/** Abre uma solicitação em rascunho. */
export function useCriarGmud() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (pedido: NovaGmudRequest) => (await api.post<DetalheGmud>('/gmuds', pedido)).data,
    onSuccess: (criada) => {
      cliente.setQueryData(chaves.detalhe(criada.id), criada)
      void cliente.invalidateQueries({ queryKey: chaves.gmuds() })
    },
  })
}

/**
 * A análise por IA de GMUD está disponível neste ambiente?
 *
 * @returns a consulta da situação
 */
export function useSituacaoDaIaDeGmud() {
  return useQuery({
    queryKey: chaves.situacaoDaIaDeGmud(),
    queryFn: async () =>
      (
        await api.get<{ disponivel: boolean; modelo: string | null; motivoIndisponivel: string | null }>(
          '/gmuds/analises-ia/situacao',
        )
      ).data,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * As revisões de código (PRs + Copilot) vinculadas à GMUD.
 *
 * Só leitura, com cache de um minuto: recarregar a página não dispara nada além de um GET.
 *
 * @param id identificador interno da solicitação
 * @returns a consulta das revisões
 */
export function useRevisoesDePr(id: number) {
  return useQuery({
    queryKey: chaves.revisoesDePr(id),
    queryFn: async () => (await api.get<RevisaoDePr[]>(`/gmuds/${id}/revisoes-de-pr`)).data,
    enabled: Number.isFinite(id),
    staleTime: 60 * 1000,
  })
}

/**
 * Os pareceres de IA da GMUD, do mais recente para o mais antigo.
 *
 * Este GET nunca gera análise — só lê pareceres gravados. Quem gera é a mutação abaixo, e
 * mesmo ela devolve o parecer existente quando nada mudou (cache por conteúdo no servidor).
 *
 * @param id identificador interno da solicitação
 * @returns a consulta dos pareceres
 */
export function usePareceresDaGmud(id: number) {
  return useQuery({
    queryKey: chaves.pareceresDaIa(id),
    queryFn: async () => (await api.get<ParecerDeGmud[]>(`/gmuds/${id}/analises-ia`)).data,
    enabled: Number.isFinite(id),
    staleTime: 60 * 1000,
  })
}

/**
 * Pede um parecer novo sobre a GMUD.
 *
 * Invalida só os pareceres desta solicitação: um parecer não muda dado de GMUD nenhum —
 * é a Regra nº 3 aparecendo até na invalidação de cache.
 *
 * @param id identificador interno da solicitação
 * @returns a mutação da análise
 */
export function useAnalisarGmud(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    // Modelo de raciocínio demora; o backend espera até 90s pela resposta.
    mutationFn: async () =>
      (await api.post<ParecerDeGmud>(`/gmuds/${id}/analises-ia`, null, { timeout: 120_000 })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.pareceresDaIa(id) })
    },
  })
}

/**
 * As observações registradas numa solicitação.
 *
 * @param id a solicitação
 * @returns a consulta
 */
export function useNotasDaGmud(id: number) {
  return useQuery({
    queryKey: [...chaves.detalhe(id), 'notas'],
    queryFn: async () => {
      const { data } = await api.get<NotaDaGmud[]>(`/gmuds/${id}/notas`)
      return data
    },
  })
}

/**
 * Registra uma observação.
 *
 * Invalida só as notas da própria solicitação: nota não muda estado, então derrubar a listagem
 * inteira de GMUDs seria tráfego sem efeito.
 *
 * @param id a solicitação
 * @returns a mutação
 */
export function useRegistrarNota(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (texto: string) => {
      const { data } = await api.post<NotaDaGmud>(`/gmuds/${id}/notas`, { texto })
      return data
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...chaves.detalhe(id), 'notas'] }),
  })
}

/**
 * A janela do comitê: quando ele analisa e a partir de quando o aprovado pode ir a produção.
 *
 * @returns a consulta
 */
export function useJanelaDoComite() {
  return useQuery({
    queryKey: chaves.janelaDoComite(),
    queryFn: async () => {
      const { data } = await api.get<JanelaDoComite>('/cab/janela')
      return data
    },
    // Muda de dia, não de minuto.
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * A integração com o GitHub está funcionando?
 *
 * A tela precisa distinguir "nenhum PR cita esta solicitação" de "ninguém foi procurar": sem essa
 * distinção, o comitê lê ausência de revisão como código sem pendência.
 *
 * @returns a consulta
 */
export function useSituacaoDasRevisoes() {
  return useQuery({
    queryKey: ['gmuds', 'revisoes-de-pr', 'situacao'],
    queryFn: async () => {
      const { data } = await api.get<{ disponivel: boolean; motivoIndisponivel: string | null }>(
        '/gmuds/revisoes-de-pr/situacao',
      )
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * As comprovações anexadas a uma solicitação.
 *
 * @param id a solicitação
 * @returns a consulta
 */
export function useAnexos(id: number) {
  return useQuery({
    queryKey: chaves.anexos(id),
    queryFn: async () => (await api.get<Anexo[]>(`/gmuds/${id}/anexos`)).data,
  })
}

/**
 * Anexa uma comprovação.
 *
 * Invalida também as AÇÕES da solicitação, e não só a lista de anexos: anexar a primeira
 * comprovação de teste é o que habilita "Validar e concluir" no servidor, e sem esta invalidação o
 * botão continuaria desabilitado até um F5 — o usuário anexaria de novo achando que não funcionou.
 *
 * @param id a solicitação
 * @returns a mutação
 */
export function useAnexar(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, categoria }: { arquivo: File; categoria: CategoriaDeAnexo }) => {
      const corpo = new FormData()
      corpo.append('arquivo', arquivo)
      corpo.append('categoria', categoria)
      const { data } = await api.post<Anexo>(`/gmuds/${id}/anexos`, corpo)
      return data
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.anexos(id) })
      void cliente.invalidateQueries({ queryKey: chaves.acoes(id) })
    },
  })
}

/**
 * Exclui uma comprovação.
 *
 * @param id a solicitação
 * @returns a mutação
 */
export function useExcluirAnexo(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (anexoId: number) => {
      await api.delete(`/gmuds/${id}/anexos/${anexoId}`)
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.anexos(id) })
      void cliente.invalidateQueries({ queryKey: chaves.acoes(id) })
    },
  })
}
